/**
 * Section Shell — consistent card frame for Standard tab sections
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, ReactNode, useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface SectionShellProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  /** Adds a collapsible header + hides body when collapsed. */
  collapsible?: boolean;
  /** Only used when `collapsible`; default is expanded. */
  defaultExpanded?: boolean;
  /** Tighter padding and vertical rhythm (nested panels, sidebars). */
  dense?: boolean;
}

const SectionShell: FC<SectionShellProps> = ({
  title,
  description,
  actions,
  children,
  collapsible = false,
  defaultExpanded = true,
  dense = false,
}) => {
  const bodyId = useId();
  const [open, setOpen] = useState(defaultExpanded);

  const titleBlock = (
    <div className="min-w-0 flex-1">
      <h4
        className={cn(
          'font-semibold text-foreground',
          dense ? 'text-[11px]' : 'text-xs',
        )}
      >
        {title}
      </h4>
      {description ? (
        <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  );

  return (
    <section
      className={cn(
        'rounded-lg border border-border/80 bg-card shadow-none',
        dense ? 'space-y-1.5 p-2' : 'space-y-2 p-3',
      )}
    >
      <header className="flex items-start justify-between gap-2">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={bodyId}
            aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
            className="flex min-w-0 flex-1 items-start gap-1.5 rounded-md text-left outline-none ring-offset-background transition-colors hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 -m-0.5 p-0.5"
          >
            <ChevronDown
              className={cn(
                'mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                !open && '-rotate-90',
              )}
              aria-hidden
            />
            {titleBlock}
          </button>
        ) : (
          titleBlock
        )}
        {actions ? (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </header>
      <div
        id={bodyId}
        className={cn(
          dense ? 'space-y-1.5' : 'space-y-2',
          collapsible && !open && 'hidden',
        )}
      >
        {children}
      </div>
    </section>
  );
};

export default SectionShell;
